/** Minimal location shape for hash-router query parsing (injectable in tests). */
export type LocationLike = Pick<Location, "hash" | "search">;

/**
 * Read a query param from hash-router URLs (`#/path?key=val`) or `location.search`.
 * Matches FMB / Sabil / Niyaaz print openers that put `receiptId` after the hash.
 */
export function getLocationSearchParam(
  name: string,
  location: LocationLike = typeof window !== "undefined"
    ? window.location
    : { hash: "", search: "" }
): string | null {
  const hash = location.hash || "";
  const q = hash.indexOf("?");
  const queryString = q >= 0 ? hash.slice(q + 1) : (location.search || "").replace(/^\?/, "");
  return new URLSearchParams(queryString).get(name);
}

export type ResolveAuthlessPrintIdInput = {
  /** Route `:id` from react-router (default source). */
  routeId?: string | undefined;
  /** When set, read this search/hash query param instead of the route id. */
  idFromSearchParam?: string;
  /** Full override; wins over route and search-param resolution. */
  resolveId?: () => string | null;
  /** Injectable location for tests / non-window contexts. */
  location?: LocationLike;
};

/**
 * Resolve the print resource id: `resolveId` → search param → route `:id`.
 * Returns null when none are present (callers treat as "No Results Found").
 */
export function resolveAuthlessPrintId({
  routeId,
  idFromSearchParam,
  resolveId,
  location,
}: ResolveAuthlessPrintIdInput): string | null {
  if (resolveId) {
    return resolveId() ?? null;
  }
  if (idFromSearchParam) {
    return getLocationSearchParam(idFromSearchParam, location);
  }
  return routeId || null;
}
